import { queryClient } from 'App';
import useAppMutation from 'hooks/useAppMutation';
import useNotify from 'hooks/useNotify';
import API from 'services/API';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import {
  CustomIntegrationSettingsType,
  IntegrationEnum,
  RemoteLockAccessibleType,
} from 'models/InternalIntegrationSetting';
import { INTEGRATION_PROPERTY_SETTINGS_QUERY_KEY } from 'hooks/useFetchIntegrationPropertySettings';
import { INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY } from 'pages/channels/integrationSettingsQuery';

const usePostLockAssignment = () => {
  const { notifyError, notifySuccess } = useNotify();
  const { t } = useTranslation();

  return useAppMutation({
    mutationFn: async (payload: {
      enumId: IntegrationEnum;
      propertyUid: string;
      lockId: string;
      integrationName: string;
      propertyName: string;
      lockName: string;
      accessibleType?: RemoteLockAccessibleType;
    }) => {
      const { enumId, propertyUid, lockId, accessibleType } = payload;

      const getPayload = () => {
        switch (enumId) {
          case IntegrationEnum.POINTCENTRAL:
            return {
              type: CustomIntegrationSettingsType.POINTCENTRAL_PROPERTY,
              assignedLock: lockId,
            };

          case IntegrationEnum.REMOTELOCK:
            return {
              type: CustomIntegrationSettingsType.REMOTELOCK_PROPERTY,
              assignedLock: lockId,
              accessibleType,
            };
          default:
            throw new Error('Invalid integration enum');
        }
      };

      const response = await API.post(
        `/api/internal/integrations/default/${enumId}/properties/${propertyUid}/lock`,
        getPayload(),
      );

      return response.data;
    },
    onSuccess: (_, { propertyName, integrationName, lockName }) => {
      notifySuccess(
        t('pageIntegrations.integrationDetails.lockConnected', {
          property: propertyName,
          integration: integrationName,
          lock: lockName,
        }),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [
          INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY,
          INTEGRATION_PROPERTY_SETTINGS_QUERY_KEY,
          'detail',
        ],
      });
    },
    onError: (error: any) => {
      notifyError(
        getErrorMessage({
          apiErrorCode: error.response?.data?.apiErrorCode,
          apiErrorMessage: error.response?.data?.apiErrorMessage,
          baseKey: 'pageIntegrations.apiErrorCodes.',
          t,
        }),
      );
    },
  });
};

export default usePostLockAssignment;
